import { Fragment, FC, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Box, Button, Flex, SxStyleProp, Text } from 'theme-ui';
import { useDispatch, useSelector } from 'react-redux';
import { Audio, Song } from 'types/Audio';
import { compose, includes, isNil, range } from 'ramda';
import { tagButtonStyle } from './AudioTool/style';
import { SongComponent, SongSkeleton, TagSkeleton } from './AudioTool/Song';
import { UploadBtn } from '../shared/UploadBtn';
import { getAudio, getAudiosCombined, getAudiosMapped, setAudio } from 'modules/audio';
import { cancelUploadUserSong, isUserSongUploading, uploadUserTrack } from 'modules/userSongs';
import useAsync from 'react-use/lib/useAsync';
import { AutoSizer, List } from 'react-virtualized';
import { FileInput, FileInputChildrenType, FileInputProps } from 'shared/FileInput';
import analytics from 'utils/analytics';
import { ACCEPTED_AUDIO } from 'constants/filetypes';
import { License, ToolName } from 'types/AppState';
import { applyMusicTagFilter, getMusicTagFilter } from 'modules/filters';

import { saveRevision } from 'modules/revisions';
import { useTranslator } from '@picsart/rc/services/localization';

import CommercialDropdown from '@picsart/rc/old/CommercialDropdown';
import { getAudios } from 'utils/apiWrapper';
import { playingSelector, usePlayer } from 'utils/PlayerContext';

export type AudioToolProps = {
  handleSelectSong: (song: Audio | null) => void;
  loading: boolean;
  songs: Song[];
  selectedSong: Audio;
  handleUnSelectSong: () => void;
  isVideoPlaying: boolean;
  selectedTag: string;
  handleSelectTag: (tag: string) => void;
};

const scrollbars: SxStyleProp = {
  scrollbarColor: '#BCC0D1 #F7F7FB',
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': { width: 4, height: 4 },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f7f7fb',
    '@media (hover: hover)': { '&:hover': { backgroundColor: '' } }
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 2,
    backgroundColor: '#bcc0d1',
    '@media (hover: hover)': { '&:hover': { backgroundColor: 'var(--x-accent-1-action)' } }
  }
};

const AudioToolBase: FC<AudioToolProps> = ({
  loading,
  songs,
  selectedSong,
  handleSelectSong,
  handleUnSelectSong,
  isVideoPlaying,
  selectedTag,
  handleSelectTag
}) => {
  const [songId, setSongId] = useState(null);
  const [tags, setTags] = useState([]);
  const [usageType, changeUsageType] = useState<number>(0);
  const translator = useTranslator();
  const [tagsHeight, setTagsHeight] = useState(0);
  const tagsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (tagsRef.current) {
      const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        const entry = entries[0];
        const {
          contentRect: { height }
        } = entry;

        setTagsHeight(height ?? 0);
      });

      resizeObserver.observe(tagsRef.current);

      return () => resizeObserver.disconnect();
    }
  }, []);

  useEffect(() => {
    if (!songs) return;

    const names = songs.map(s => s.artistName);
    const namesSingle = new Set(names);
    setTags(Array.from(namesSingle));
  }, [songs]);

  useEffect(() => {
    isVideoPlaying && setSongId(null);
  }, [isVideoPlaying]);

  const getFilteredSong = useMemo(() => {
    if (!songs) {
      return [];
    }
    const license = usageType === 1 ? License.Commercial : License.Personal;

    const songsByLicense = songs?.filter(() => license === License.Personal);
    return selectedTag
      ? songsByLicense?.filter(item => includes(selectedTag, item.artistName))
      : songsByLicense;
  }, [selectedTag, loading, songs, usageType]);

  const rowRenderer = useCallback(
    ({ index, style }) => {
      const item = getFilteredSong?.[index];

      return item ? (
        <div style={style}>
          <SongComponent
            {...item}
            coverUrl={item.cover_url}
            handleUnSelectSong={handleUnSelectSong}
            handleSelectSong={handleSelectSong}
            duration={Number(item.duration)}
            setSongId={setSongId}
            songName={item.songName}
            isSelected={
              !isNil(selectedSong) &&
              `${item.url}${item.songName}` === `${selectedSong.url}${selectedSong.songName}`
            }
            active={item.id === songId && !isVideoPlaying}
            key={item.id}
          />
        </div>
      ) : null;
    },
    [getFilteredSong, handleSelectSong, handleUnSelectSong, songId]
  );

  return (
    <Fragment>
      <Flex sx={{ flexWrap: 'wrap', mb: 2 }} ref={tagsRef}>
        <Button
          data-test="All"
          sx={tagButtonStyle(isNil(selectedTag))}
          onClick={() => handleSelectTag(null)}
          variant="secondary"
        >
          {translator('All')}
        </Button>
        {tags.length
          ? tags.map((item: string) => (
              <Button
                key={item}
                sx={tagButtonStyle(selectedTag === item)}
                onClick={() => handleSelectTag(item)}
                variant="secondary"
                data-test={item}
              >
                {translator(item)}
              </Button>
            ))
          : range(0, 10).map(item => <TagSkeleton key={item} />)}
      </Flex>
      <Flex
        sx={{
          justifyContent: 'flex-end'
        }}
      >
        <CommercialDropdown
          value={usageType}
          onChange={(_, type) => {
            changeUsageType(type);
          }}
        />
      </Flex>
      <Box sx={{ height: `calc(100vh - ${tagsHeight + 120}px)`, mt: 1, paddingBottom: 100 }}>
        {!loading ? (
          <AutoSizer>
            {({ width, height }) => (
              <List
                width={width}
                height={height}
                rowCount={getFilteredSong.length}
                rowRenderer={rowRenderer}
                rowHeight={64}
                sx={{ mr: 2, ...scrollbars }}
                overscanRowCount={4}
              />
            )}
          </AutoSizer>
        ) : (
          range(0, 16).map(item => <SongSkeleton key={item} />)
        )}
      </Box>
    </Fragment>
  );
};

const UploadUserTrack = () => {
  const dispatch = useDispatch();
  const loading = useSelector(isUserSongUploading);
  const handleUpload: FileInputProps['onChange'] = files => {
    dispatch(uploadUserTrack(files[0]));
  };
  const translator = useTranslator();
  const handleClick = () => {
    analytics.upload('music');
  };

  return (
    <Fragment>
      <FileInput onChange={handleUpload} accept={ACCEPTED_AUDIO}>
        {(props: FileInputChildrenType) => (
          <UploadBtn onClickHandler={handleClick} loading={loading} {...props}>
            {translator('Upload')}
          </UploadBtn>
        )}
      </FileInput>
      <Text
        variant="body"
        sx={{
          color: 'grey7',
          fontSize: '10px',
          opacity: 0.6,
          fontWeight: 'heading',
          mt: 2,
          textAlign: 'center'
        }}
      >
        {translator('or drag and drop your files here')}
      </Text>
    </Fragment>
  );
};

export const AudioTool = () => {
  const dispatch = useDispatch();
  const player = usePlayer();

  const handleSelectSong: (song: Audio) => void = song => {
    analytics.musicAction({
      is_premium: song.isPaid,
      item_id: song.id,
      result_source: 'epidemic',
      music_duration: Math.ceil(song.duration / 1000),
      category_name: song.artistName
    });
    player.pause();
    dispatch(cancelUploadUserSong());
    dispatch(setAudio(song));
    dispatch(saveRevision(ToolName.Music));
  };
  const handleUnSelectSong: () => void = () => {
    analytics.musicAction({
      action: 'remove'
    });
    player.pause();
    dispatch(cancelUploadUserSong());
    dispatch(setAudio(null));
    dispatch(saveRevision(ToolName.Music));
  };

  const audio = useSelector(getAudio);

  const { value: songs, loading } = useAsync(async () => {
    const songs = await getAudios();
    return compose(getAudiosCombined, getAudiosMapped)(songs);
  }, []);

  const isVideoPlaying = usePlayer(playingSelector);
  const selectedFilterTag = useSelector(getMusicTagFilter);
  const [selectedTag, setSelectedTag] = useState(selectedFilterTag);
  const handleSelectTag = useCallback((tag: string) => {
    dispatch(applyMusicTagFilter(tag));
    setSelectedTag(tag);
  }, []);
  return (
    <Fragment>
      <AudioToolBase
        loading={loading}
        songs={songs}
        selectedSong={audio}
        handleSelectSong={handleSelectSong}
        handleUnSelectSong={handleUnSelectSong}
        isVideoPlaying={isVideoPlaying}
        selectedTag={selectedTag}
        handleSelectTag={handleSelectTag}
      />
      <Box sx={{ position: 'fixed', left: 108, bottom: '10px', width: 313 }}>
        <UploadUserTrack />
      </Box>
    </Fragment>
  );
};
